import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, getUser } from "../lib/nurseryApi";
import { PaperPlaneTilt, Leaf, Sparkle, Lock, ChatCircleDots } from "@phosphor-icons/react";

function sessionId() {
  let s = localStorage.getItem("nurseryai_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem("nurseryai_chat_session", s);
  }
  return s;
}

export default function ChatbotPage() {
  const user = getUser();
  const nurseryName = user?.nursery_name || "Your Nursery";
  const enabled = !!user?.chatbot_enabled;

  const [messages, setMessages] = useState(enabled ? [
    { role: "assistant", content: `Hi there! I'm the AI plant advisor for ${nurseryName}, powered by NurserySense. Ask me anything — what plant for shade, clay soil, fast privacy hedge — I'll help you find it. I can also pass your name and number along so the nursery follows up.` },
  ] : []);
  const [input, setInput] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const msg = input;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot", {
        session_id: sessionId(),
        nursery_id: user?.id,
        message: msg,
        customer_name: name || null,
        customer_contact: contact || null,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — having trouble reaching the AI. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  // Upsell state: chatbot add-on not enabled for this nursery
  if (!enabled) {
    return (
      <div data-testid="chatbot-upsell-page" className="fade-up">
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Customer Front Desk</div>
        <h1 className="page-title" style={{ marginTop: 8 }}>Customer Chatbot</h1>
        <p style={{ color: "var(--muted-fg)", marginTop: 8, marginBottom: 28, maxWidth: 720, fontSize: 15 }}>
          A 24/7 AI plant advisor embeddable on your website. Branded with <b>{nurseryName}</b>, powered by NurserySense.
        </p>
        <div className="card" style={{ padding: 36, background: "linear-gradient(160deg, var(--primary), #3F5640)", color: "white", border: "none", maxWidth: 720 }}>
          <span className="badge" style={{ background: "var(--accent)", color: "var(--text)" }}><Lock size={11} weight="bold" /> Add-on · not enabled</span>
          <h2 className="font-display" style={{ fontSize: 36, marginTop: 16, color: "white", lineHeight: 1.1 }}>Turn after-hours questions into morning leads.</h2>
          <p style={{ fontSize: 16, color: "#dceadc", marginTop: 14, lineHeight: 1.55 }}>
            The AI chatbot answers "what plant for shade and clay soil?" 24/7, captures every name & number, and texts them to your phone. Embeddable on your site as a script tag.
          </p>
          <ul style={{ marginTop: 18, paddingLeft: 18, fontSize: 14.5, lineHeight: 1.8, color: "#dceadc" }}>
            <li>Branded with your nursery name on every conversation</li>
            <li>Trained on your plant catalog and Tennessee growing zones</li>
            <li>Captures leads even when your shop is closed</li>
            <li>Usage-priced add-on (AI runs on real costs)</li>
          </ul>
          <div style={{ marginTop: 22, padding: 16, background: "rgba(255,255,255,0.12)", borderRadius: 12, fontSize: 13.5 }}>
            <b>Want it on?</b> Reply to your NurserySense onboarder, or call the platform line. We'll enable it in under 60 seconds.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="chatbot-page" className="fade-up">
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Customer Front Desk</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Customer Chatbot</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 8, marginBottom: 24, maxWidth: 720, fontSize: 15 }}>
        This is what your customers see when they chat from your website. Every conversation is captured, every name + contact lands in your <Link to="/app/leads" style={{ color: "var(--secondary)", textDecoration: "underline" }}>Leads</Link> page.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }} className="chat-grid">
        <div className="card" style={{ display: "flex", flexDirection: "column", height: "70vh", minHeight: 480, overflow: "hidden" }}>
          {/* Chat header — NURSERY brand on top, "Powered by NurserySense" below */}
          <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, var(--primary), #3F5640)", color: "white", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.2)", display: "grid", placeItems: "center" }}>
              <Leaf size={20} weight="fill" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{nurseryName} · Plant Advisor</div>
              <div style={{ fontSize: 10.5, opacity: 0.75, display: "flex", alignItems: "center", gap: 4 }}>
                <Sparkle size={10} weight="fill" /> Powered by NurserySense · usually replies in seconds
              </div>
            </div>
          </div>
          <div style={{ flex: 1, padding: 24, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, background: "var(--bg-alt)" }}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-bubble ${m.role === "user" ? "me" : ""}`} data-testid={`chat-msg-${i}`}>{m.content}</div>
            ))}
            {loading && <div className="chat-bubble" style={{ fontStyle: "italic", color: "var(--muted-fg)" }}>typing…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{ display: "flex", gap: 8, padding: 16, background: "var(--surface)", borderTop: "1px solid var(--border)" }}>
            <input className="input" style={{ flex: 1 }} placeholder="Ask about plants, prices, availability…" value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input" />
            <button className="btn btn-primary" type="submit" disabled={loading || !input.trim()} data-testid="chat-send-btn">
              <PaperPlaneTilt size={16} weight="bold" /> Send
            </button>
          </form>
          <div style={{ padding: "10px 20px", background: "var(--surface)", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--muted-fg)", textAlign: "center" }}>
            <Sparkle size={10} weight="fill" style={{ verticalAlign: -1, color: "var(--secondary)" }} /> Powered by <b style={{ color: "var(--secondary)" }}>NurserySense</b>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card" style={{ padding: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)" }}>Lead capture</div>
            <h3 className="font-display" style={{ fontSize: 22, marginTop: 6 }}>Leave your details</h3>
            <p style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 6, lineHeight: 1.5 }}>Optional. If filled, your next message saves as a lead so {nurseryName} can follow up.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
              <div>
                <label className="label">Name</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="lead-name-input" />
              </div>
              <div>
                <label className="label">Phone or email</label>
                <input className="input" value={contact} onChange={(e) => setContact(e.target.value)} data-testid="lead-contact-input" />
              </div>
            </div>
          </div>
          <div className="card" style={{ padding: 22, background: "var(--text)", color: "var(--bg)", border: "none" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent)" }}>Try asking</div>
            <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.8, color: "rgba(255,255,255,0.85)" }}>
              <li>What's a fast-growing privacy hedge for clay soil?</li>
              <li>Do you have Eastern Redbuds in stock?</li>
              <li>Best shade tree for a small Tennessee yard?</li>
              <li>Wholesale pricing on Knockout Roses?</li>
            </ul>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .chat-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
